import {
  Box,
  Icon,
  Text,
  colors,
  useClassNames,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import * as styles from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/ShiftCellActions/index.vanilla.css'
import TooltipWrapper from 'modules/shift_management/components/ShiftManagement/TooltipWrapper'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useDestroyRestDay } from 'modules/shift_management/hooks/useDestroyRestDays'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import type { RestDay, ShiftsRow } from 'modules/shift_management/lib/types'

import BaseCell from '../BaseCell'

interface Props {
  restDay: RestDay
  shiftsRow: ShiftsRow
  isSelectionEnabled: boolean
  isSelected: boolean
}

const RestDayLayout = ({ restDay, shiftsRow, isSelectionEnabled, isSelected }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { viewOptions } = useCurrentViewOptions()
  const {
    schedule: { showCompactView },
  } = viewOptions

  const { canManage } = shiftsRow
  const readOnly = !canManage
  const { onDestroy } = useDestroyRestDay(restDay)
  const { closePortal } = usePortal()
  const [isHovering, toggleHovering] = useToggle(false)
  const cx = useClassNames(styles)
  const draftColor = view.isDaily ? colors.grey000White : colors.lighter000
  const background = restDay.isDraft ? draftColor : colors.fuchsia200

  const isRestDayOfCurrentUser = currentEmployee?.id === shiftsRow.id

  const restDayPlannerInfo = i18n.t('shift_management.cells.rest_day.info')
  const restDayEmployeeInfo = i18n.t('shift_management.cells.rest_day.info_employee')

  const doNotShowTooltip = restDay.isPublished && !isRestDayOfCurrentUser

  const toolTipLabel =
    restDay.isPublished && isRestDayOfCurrentUser ? restDayEmployeeInfo : restDayPlannerInfo

  const onDeleteAction = (event: MouseEvent) => {
    event.stopPropagation()
    onDestroy(closePortal)
  }

  const onSelect = () => {
    if (readOnly) return

    dispatchSelection({
      type: 'TOGGLE_REST_DAY',
      restDay,
      view,
    })
  }

  return (
    <Box
      style={{
        backgroundColor: background,
      }}
      cursor={!readOnly ? 'pointer' : 'default'}
      onClick={onSelect}
      borderRadius={{ all: 'abs008' }}
      className={cx(
        'relative',
        { 'border.solid.grey800': isSelected },
        { 'border.solid.fuchsia1000': !isSelected && restDay.isDraft }
      )}
      height={view.cellMaxHeight}
      maxHeight={showCompactView ? view.cellMinHeight : view.cellMaxHeight}
      minHeight={view.cellMinHeight}
    >
      <BaseCell alignItems="flexStart" justifyContent="flexStart">
        {doNotShowTooltip ? (
          <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
            {i18n.t('shift_management.cells.rest_day.title')}
          </Text.Normal>
        ) : (
          <TooltipWrapper.Alt attachment="bottom center" label={toolTipLabel}>
            <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
              {i18n.t('shift_management.cells.rest_day.title')}
            </Text.Normal>
          </TooltipWrapper.Alt>
        )}
      </BaseCell>
      <Box
        style={{ visibility: isSelectionEnabled ? 'hidden' : 'visible', right: '0' }}
        flexDirection="row"
        cursor="pointer"
        height="full"
        alignItems="flexStart"
        justifyContent="flexEnd"
        position="absolute"
        flexGrow
        gap="s2"
        onMouseEnter={toggleHovering(true)}
        onMouseLeave={toggleHovering(false)}
      >
        {!isSelected && !isSelectionEnabled && !readOnly && (
          <Box
            flexDirection="row"
            cursor="pointer"
            background="grey800"
            height="full"
            alignItems="center"
            justifyContent="center"
            borderRadius={{ topRight: 'abs006', bottomRight: 'abs006' }}
            flexGrow
            paddingX="s4"
            gap="s2"
            className={cx({ hidden: !isHovering })}
          >
            <Box onClick={onDeleteAction} height="full" justifyContent="center" padding="s2">
              <Icon.Small icon="delete" color="grey000White" />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default observer(RestDayLayout)
